/**
 * Pagination for the Judge.me all-reviews widget.
 *
 * The app renders that widget with infinite scroll, which drops hundreds of
 * reviews on the homepage. This replaces it with the theme's paginated control
 * and fetches 10 reviews per page from the same endpoint the widget uses,
 * reusing its current sort and rating filter so both stay in sync.
 */
(function () {
  'use strict';

  var PER_PAGE = 10;
  var WINDOW_SIZE = 1;
  var ICON_PREV =
    '<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 0.818115L1 5.81812L6 10.8181" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_NEXT =
    '<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 0.818115L6 5.81812L1 10.8181" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function jq() {
    return window.jdgm && window.jdgm.$;
  }

  function apiReady() {
    return !!(jq() && window.jdgm && typeof window.jdgm.ajaxParamsFor === 'function');
  }

  function Pager(article) {
    this.article = article;
    this.page = 1;
    this.lastPage = null;
    this.loading = false;
    this.currentSignature = null;
    this.suppressUntil = 0;
    this.nav = null;
    this.pendingCheck = null;
  }

  Pager.prototype.tab = function () {
    var active = this.article.querySelector('.jdgm-subtab__name.jdgm--active');
    return active && active.getAttribute('data-tabname') === 'shop-reviews' ? 'shop-reviews' : 'product-reviews';
  };

  Pager.prototype.list = function () {
    return this.tab() === 'shop-reviews'
      ? this.article.querySelector('.jdgm-shop-reviews__body')
      : this.article.querySelector('.jdgm-all-reviews__body');
  };

  Pager.prototype.data = function () {
    var $ = jq();
    return ($ && $(this.article).data()) || {};
  };

  /**
   * Judge.me's infinite loader does not use the visible spinner as its trigger.
   * It listens to window scroll and only stops when the open list has this
   * jQuery data flag, so CSS alone cannot disable it.
   */
  Pager.prototype.stopInfiniteScroll = function () {
    var $ = jq();
    if (!$) return;

    ['.jdgm-all-reviews__body', '.jdgm-shop-reviews__body'].forEach(
      function (selector) {
        var list = this.article.querySelector(selector);
        if (!list) return;
        $(list).data('all-reviews-loaded', true);
        list.setAttribute('data-all-reviews-loaded', 'true');
      }.bind(this)
    );
  };

  Pager.prototype.params = function () {
    var params = window.jdgm.ajaxParamsFor(jq()(this.article)) || {};
    var out = {};
    Object.keys(params).forEach(function (key) {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') out[key] = params[key];
    });
    out.review_type = this.tab();
    out.per_page = PER_PAGE;
    out.page = this.page;
    return out;
  };

  /** Identifies the review set on screen, so tab / sort / filter changes are detectable. */
  Pager.prototype.signatureOf = function () {
    var params = this.params();
    return [params.review_type, params.sort_by, params.sort_dir, params.filter_rating, params.search].join('|');
  };

  /**
   * The response only says which page it returned, so the page count comes from
   * the widget's own totals: the tab count normally, or the matching histogram
   * row when a rating filter is on. A short page corrects it afterwards.
   */
  Pager.prototype.totalPages = function () {
    if (this.lastPage) return this.lastPage;

    var data = this.data();
    var rating = parseInt(data.filterRating, 10);
    var total;

    if (rating >= 1 && rating <= 5) {
      var row = this.article.querySelectorAll('.jdgm-histogram__row')[5 - rating];
      var frequency = row && row.querySelector('.jdgm-histogram__frequency');
      if (frequency) total = parseInt(frequency.textContent.replace(/\D/g, ''), 10);
    } else {
      total = this.tab() === 'shop-reviews' ? data.numberOfShopReviews : data.numberOfProductReviews;
    }

    if (!total || isNaN(total)) return 1;
    return Math.max(1, Math.ceil(total / PER_PAGE));
  };

  Pager.prototype.pageParts = function (totalPages) {
    var parts = [];
    var current = this.page;

    for (var page = 1; page <= totalPages; page++) {
      var isEdge = page === 1 || page === totalPages;
      var isNear = Math.abs(page - current) <= WINDOW_SIZE;
      if (isEdge || isNear) {
        parts.push(page);
      } else if (parts[parts.length - 1] !== '…') {
        parts.push('…');
      }
    }

    return parts;
  };

  Pager.prototype.buildNav = function () {
    var totalPages = this.totalPages();
    if (totalPages < 2) {
      if (this.nav) this.nav.remove();
      this.nav = null;
      return;
    }

    if (!this.nav) {
      this.nav = document.createElement('nav');
      this.nav.className = 'lm-reviews-pagination pagination pagination-type--paginated';
      this.nav.setAttribute('aria-label', 'Paginación de reseñas');
    }

    var self = this;
    var html = '<div class="page-numbers nav-links">';

    html +=
      '<span class="prev"><a href="#" data-lm-page="' +
      (this.page - 1) +
      '"' +
      (this.page === 1 ? ' disabled' : '') +
      '>' +
      ICON_PREV +
      '<span class="visually-hidden">Anterior</span></a></span>';

    this.pageParts(totalPages).forEach(function (part) {
      if (part === '…') {
        html += '<span class="page">…</span>';
      } else if (part === self.page) {
        html += '<span class="page current" aria-current="page">' + part + '</span>';
      } else {
        html += '<span class="page"><a href="#" data-lm-page="' + part + '">' + part + '</a></span>';
      }
    });

    html +=
      '<span class="next"><a href="#" data-lm-page="' +
      (this.page + 1) +
      '"' +
      (this.page === totalPages ? ' disabled' : '') +
      '>' +
      ICON_NEXT +
      '<span class="visually-hidden">Siguiente</span></a></span>';

    this.nav.innerHTML = html + '</div>';

    var list = this.list();
    if (list && list.parentNode && this.nav.previousElementSibling !== list) {
      list.parentNode.insertBefore(this.nav, list.nextSibling);
    }
  };

  Pager.prototype.trimToPageSize = function () {
    var list = this.list();
    if (!list) return 0;
    var reviews = list.querySelectorAll('.jdgm-rev');
    for (var i = PER_PAGE; i < reviews.length; i++) reviews[i].remove();
    return reviews.length;
  };

  /** Page 1 is already in the DOM with the right sort, so it never needs a fetch. */
  Pager.prototype.reset = function () {
    var list = this.list();
    if (!list || !list.querySelector('.jdgm-rev')) return;
    this.suppressUntil = Date.now() + 500;
    this.page = 1;
    this.currentSignature = this.signatureOf();
    var loaded = this.trimToPageSize();
    this.lastPage = loaded && loaded <= PER_PAGE ? 1 : null;
    this.buildNav();
  };

  Pager.prototype.goTo = function (page) {
    var self = this;
    var list = this.list();
    var url = this.data().url;
    if (!list || !url || this.loading || page === this.page || page < 1) return;

    var previousPage = this.page;
    this.page = page;
    this.loading = true;
    this.article.classList.add('lm-reviews-loading');

    var query = [];
    var params = this.params();
    Object.keys(params).forEach(function (key) {
      query.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
    });

    fetch(url + '?' + query.join('&'), { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('Judge.me responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        self.suppressUntil = Date.now() + 500;
        list.innerHTML = data.html || '';
        if (typeof window.jdgm.customizeReviews === 'function') window.jdgm.customizeReviews();
        self.stopInfiniteScroll();
        var rendered = list.querySelectorAll('.jdgm-rev').length;
        self.lastPage = rendered < PER_PAGE ? self.page : null;
        self.buildNav();
        self.scrollToTop();
        window.DtcEsTracking?.review('paginate');
      })
      .catch(function () {
        self.page = previousPage;
      })
      .then(function () {
        self.loading = false;
        self.article.classList.remove('lm-reviews-loading');
      });
  };

  Pager.prototype.scrollToTop = function () {
    var offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
    var top = window.pageYOffset + this.article.getBoundingClientRect().top - ((offset || 0) + 20);
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  /**
   * The widget still owns the list: it re-renders it on tab, sort and filter changes,
   * and an in-flight infinite-scroll request can land after we take over. Mutation
   * callbacks are async, so a boolean "am I rendering" flag would already be false by
   * the time they run; a short suppression window is what actually holds.
   */
  Pager.prototype.watch = function () {
    var self = this;
    var observer = new MutationObserver(function () {
      self.stopInfiniteScroll();
      clearTimeout(self.pendingCheck);
      var delay = Math.max(150, self.suppressUntil - Date.now() + 25);
      self.pendingCheck = setTimeout(function () {
        self.stopInfiniteScroll();
        if (self.loading) return;
        var list = self.list();
        if (!list || list.querySelectorAll('.jdgm-rev').length <= PER_PAGE) return;
        // A different tab, sort or rating filter means a new set: back to page one.
        // Otherwise it is leftover loading, so keep the visitor where they were.
        if (self.signatureOf() !== self.currentSignature) self.reset();
        else self.trimToPageSize();
      }, delay);
    });

    ['.jdgm-all-reviews__body', '.jdgm-shop-reviews__body'].forEach(function (selector) {
      var list = self.article.querySelector(selector);
      if (list) observer.observe(list, { childList: true });
    });

    this.article.addEventListener('click', function (event) {
      var link = event.target.closest('.lm-reviews-pagination a[data-lm-page]');
      if (!link) return;
      event.preventDefault();
      if (link.hasAttribute('disabled')) return;
      self.goTo(parseInt(link.getAttribute('data-lm-page'), 10));
    });
  };

  function setup(article) {
    if (article.dataset.lmPaginated === 'true') return;
    if (!apiReady() || !article.classList.contains('jdgm--done-setup-widget')) return;

    var pager = new Pager(article);
    if (!pager.list() || !pager.list().querySelector('.jdgm-rev')) return;

    article.dataset.lmPaginated = 'true';
    article.classList.add('lm-reviews-paged');
    pager.stopInfiniteScroll();
    pager.reset();
    pager.watch();
  }

  function run() {
    document.querySelectorAll('article.jdgm-all-reviews-widget').forEach(setup);
  }

  function poll() {
    var attempts = 0;
    var timer = setInterval(function () {
      run();
      attempts++;
      if (attempts > 40 || document.querySelector('article.jdgm-all-reviews-widget[data-lm-paginated="true"]')) {
        clearInterval(timer);
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', poll);
  } else {
    poll();
  }
})();
